(() => {
  'use strict';

  /* =========================================================
     VSX9 PREMIUM WISHLIST
     Fully compatible with Shopify themes and product card wishlists

     Supported storage keys:
     - vsx9_wishlist_v1 (primary)
     - vs_tru_pahadi_wishlist_v1 (store card sync)

     Shared event:
     - vs:wishlist:updated
  ========================================================= */

  const STORAGE_KEYS = ['vsx9_wishlist_v1', 'vs_tru_pahadi_wishlist_v1'];
  const PRIMARY_KEY = STORAGE_KEYS[0];

  const selectedItems = new Set();
  const quantities = {};
  const productCache = new Map();
  const inflightRequests = new Map();
  let toastTimer = null;
  let updateDebounceTimer = null;
  let isUpdatingStock = false;


  /* =========================================================
     STORAGE
  ========================================================= */

  function getWishlist() {
    const combined = [];
    const seen = new Set();

    for (const key of STORAGE_KEYS) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;

        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          parsed.forEach((rawItem, index) => {
            const item = normalizeItem(rawItem, index);
            if (!item) return;

            const identifier = String(item.id || item.handle || '');
            if (identifier) {
              if (!seen.has(identifier)) {
                seen.add(identifier);
                combined.push(item);
              }
            } else {
              combined.push(item);
            }
          });
        }
      } catch (error) {
        console.error(`VS Wishlist storage error on key "${key}":`, error);
      }
    }
    return combined;
  }

  function normalizeItem(raw, index) {
    if (!raw) return null;

    // Support simple ID or handle strings stored in localStorage
    if (typeof raw === 'string' || typeof raw === 'number') {
      const idOrHandle = String(raw).trim();
      const isNumeric = /^\d+$/.test(idOrHandle);
      return {
        id: isNumeric ? idOrHandle : '',
        handle: isNumeric ? '' : idOrHandle,
        title: '',
        price: 0,
        image: '',
        url: isNumeric ? '' : `/products/${idOrHandle}`,
        _raw: raw
      };
    }

    if (typeof raw === 'object') {
      const id = raw.id ? String(raw.id) : (raw.productId ? String(raw.productId) : (raw.variantId ? String(raw.variantId) : ''));
      const handle = raw.handle ? String(raw.handle) : '';
      return {
        id,
        variantId: raw.variantId ? String(raw.variantId) : '',
        handle,
        title: raw.title ? String(raw.title) : '',
        price: raw.price != null ? raw.price : 0,
        image: raw.image || raw.featured_image || raw.featuredImage || raw.image_url || '',
        url: raw.url || (handle ? `/products/${encodeURIComponent(handle)}` : ''),
        vendor: raw.vendor || '',
        available: raw.available != null ? Boolean(raw.available) : undefined,
        _raw: raw
      };
    }

    return null;
  }

  function saveWishlist(items, broadcast = true) {
    try {
      localStorage.setItem(PRIMARY_KEY, JSON.stringify(items));

      try {
        const idList = items.map((it) => String(it.id || it.handle || '')).filter(Boolean);
        localStorage.setItem(STORAGE_KEYS[1], JSON.stringify(idList));
      } catch (_) {}
    } catch (error) {
      console.error('VS Wishlist save error:', error);
    }

    if (broadcast) {
      document.dispatchEvent(
        new CustomEvent('vs:wishlist:updated', {
          detail: { items }
        })
      );
    }
  }


  /* =========================================================
     HELPERS
  ========================================================= */

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function productKey(item, index) {
    if (!item) return String(index ?? 0);
    return String(item.id || item.variantId || item.handle || index);
  }

  function getRoot(root) {
    if (root?.dataset?.vsRootUrl) {
      const r = root.dataset.vsRootUrl;
      return r.endsWith('/') ? r : r + '/';
    }
    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root;
    }
    return '/';
  }

  function getCartAddUrl(root) {
    return root?.dataset?.vsCartAddUrl || getRoot(root) + 'cart/add.js';
  }

  function parsePrice(value) {
    if (value == null) return 0;
    if (typeof value === 'number') {
      return isNaN(value) || !Number.isFinite(value) ? 0 : value;
    }
    const str = String(value).trim();
    // Match the first valid number group like '2,100.00' avoiding prefixes like 'Rs.'
    const match = str.match(/[0-9][0-9,]*(\.[0-9]+)?/);
    if (!match) return 0;
    const num = parseFloat(match[0].replace(/,/g, ''));
    return isNaN(num) || !Number.isFinite(num) ? 0 : num;
  }

  function formatMoney(value, root) {
    if (value == null) return '';

    const amount = parsePrice(value);

    const currencyCode =
      root?.dataset?.vsCurrency ||
      (window.Shopify && Shopify.currency && Shopify.currency.active) ||
      'INR';
    const currencySymbol = root?.dataset?.vsCurrencySymbol || '₹';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      }).format(amount);
    } catch (_) {
      const fixed = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
      return currencySymbol ? `${currencySymbol}${fixed}` : `${currencyCode} ${fixed}`;
    }
  }


  /* =========================================================
     PRODUCT DATA & VARIANT RESOLUTION (WITH CACHE)
  ========================================================= */

  async function fetchProductData(item, root) {
    if (!item) return null;

    const cacheKey = item.handle || item.id;
    if (cacheKey && productCache.has(cacheKey)) {
      return productCache.get(cacheKey);
    }

    if (cacheKey && inflightRequests.has(cacheKey)) {
      return inflightRequests.get(cacheKey);
    }

    const promise = (async () => {
      // 1. Try by handle
      if (item.handle) {
        try {
          const res = await fetch(`${getRoot(root)}products/${encodeURIComponent(item.handle)}.js`, {
            method: 'GET',
            headers: { Accept: 'application/json' }
          });
          if (res.ok) {
            const product = await res.json();
            productCache.set(item.handle, product);
            if (product.id) productCache.set(String(product.id), product);
            return product;
          }
        } catch (_) {}
      }

      // 2. Try suggest search by numeric ID if handle was not present
      if (item.id && /^\d+$/.test(item.id)) {
        try {
          const suggestUrl = `${getRoot(root)}search/suggest.json?q=id:${encodeURIComponent(item.id)}&resources[type]=product`;
          const res = await fetch(suggestUrl, {
            method: 'GET',
            headers: { Accept: 'application/json' }
          });
          if (res.ok) {
            const data = await res.json();
            const p = data?.resources?.results?.products?.[0];
            if (p) {
              if (p.handle) item.handle = p.handle;
              const productObj = {
                id: p.id || item.id,
                title: p.title,
                handle: p.handle,
                price: p.price,
                featured_image: p.image || p.featured_image?.url || '',
                url: p.url || (p.handle ? `${getRoot(root)}products/${encodeURIComponent(p.handle)}` : ''),
                vendor: p.vendor || '',
                available: p.available !== false,
                variants: p.variants && p.variants.length ? p.variants : [{ id: p.id, available: p.available !== false }]
              };
              productCache.set(String(item.id), productObj);
              if (p.handle) productCache.set(p.handle, productObj);

              // Background fetch for full variant details
              if (p.handle) {
                fetch(`${getRoot(root)}products/${encodeURIComponent(p.handle)}.js`, { headers: { Accept: 'application/json' } })
                  .then((r) => r.ok ? r.json() : null)
                  .then((fullP) => {
                    if (fullP) {
                      productCache.set(p.handle, fullP);
                      productCache.set(String(item.id), fullP);
                    }
                  })
                  .catch(() => {});
              }

              return productObj;
            }
          }
        } catch (_) {}
      }

      return null;
    })();

    if (cacheKey) inflightRequests.set(cacheKey, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      if (cacheKey) inflightRequests.delete(cacheKey);
    }
  }

  async function getVariant(item, root) {
    if (item.variantId) {
      return {
        id: item.variantId,
        available: item.available !== false
      };
    }

    const product = await fetchProductData(item, root);
    if (!product || !product.variants || !product.variants.length) {
      return null;
    }

    return product.variants.find((v) => v.available) || product.variants[0];
  }


  /* =========================================================
     HEADER & BADGE COUNT
  ========================================================= */

  function updateWishlistCount() {
    const items = getWishlist();
    const count = items.length;

    // Section count badges
    document.querySelectorAll('[data-vs-wishlist-count]').forEach((el) => {
      el.textContent = count;
    });

    // Theme header counters & badges
    document.querySelectorAll('.vsx9-wishlist-count, [data-wishlist-count], .vs-wishlist__count-number, .vsx9-header-wishlist-count').forEach((el) => {
      el.textContent = count;
      if (el.classList.contains('vsx9-header-wishlist-count')) {
        el.classList.toggle('is-empty', count === 0);
      }
    });
  }


  /* =========================================================
     ROW TEMPLATE
  ========================================================= */

  function createRow(item, index, root) {
    const key = productKey(item, index);
    const title = escapeHTML(item.title || 'Wishlist Product');
    const image = item.image || item.featured_image || '';
    const url = item.url || (item.handle ? `${getRoot(root)}products/${encodeURIComponent(item.handle)}` : '#');
    const price = item.price && parsePrice(item.price) >= 1 ? formatMoney(item.price, root) : '...';
    const quantity = quantities[key] || 1;
    const selected = selectedItems.has(key) ? 'checked' : '';
    const addText = root?.dataset?.vsAddText || 'Add To Cart';
    const buyText = root?.dataset?.vsBuyText || 'Buy Now';

    return `
      <div
        class="vs-wishlist__row"
        data-vs-wishlist-row
        data-product-key="${escapeHTML(key)}"
      >
        <!-- PRODUCT -->
        <div class="vs-wishlist__product">
          <label class="vs-wishlist__checkbox-wrap" aria-label="Select ${title}">
            <input
              type="checkbox"
              class="vs-wishlist__checkbox"
              data-vs-product-select
              data-key="${escapeHTML(key)}"
              ${selected}
            >
            <span class="vs-wishlist__custom-checkbox"></span>
          </label>

          <a
            href="${escapeHTML(url)}"
            class="vs-wishlist__image vs-wishlist__product-image-wrap"
            aria-label="${title}"
          >
            ${
              image
                ? `
                  <img
                    src="${escapeHTML(image)}"
                    alt="${title}"
                    loading="lazy"
                    class="vs-wishlist__product-image"
                    data-vs-product-image
                  >
                `
                : `
                  <div class="vs-wishlist__image-placeholder vs-wishlist__product-image--empty" data-vs-product-image-empty>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                `
            }
          </a>

          <div class="vs-wishlist__product-info">
            <a
              href="${escapeHTML(url)}"
              class="vs-wishlist__product-title"
              data-vs-product-title
            >
              ${title}
            </a>

            <div class="vs-wishlist__product-meta" data-vs-product-meta>
              ${item.vendor ? `<span>${escapeHTML(item.vendor)}</span><span class="vs-wishlist__product-meta-dot"></span>` : ''}
              <span>Saved to wishlist</span>
            </div>
          </div>
        </div>

        <!-- QUANTITY -->
        <div class="vs-wishlist__quantity">
          <div class="vs-wishlist__quantity-box vs-wishlist__quantity-control">
            <button
              type="button"
              class="vs-wishlist__quantity-button"
              data-vs-quantity-minus
              data-key="${escapeHTML(key)}"
              aria-label="Decrease quantity"
            >−</button>

            <input
              type="number"
              class="vs-wishlist__quantity-input"
              value="${quantity}"
              min="1"
              max="99"
              data-vs-quantity
              data-key="${escapeHTML(key)}"
              aria-label="Quantity for ${title}"
            >

            <button
              type="button"
              class="vs-wishlist__quantity-button"
              data-vs-quantity-plus
              data-key="${escapeHTML(key)}"
              aria-label="Increase quantity"
            >+</button>
          </div>
        </div>

        <!-- PRICE -->
        <div class="vs-wishlist__price">
          <span class="vs-wishlist__price-current" data-vs-product-price>
            ${escapeHTML(price)}
          </span>
        </div>

        <!-- STOCK STATUS -->
        <div class="vs-wishlist__stock">
          <span
            class="vs-wishlist__stock-pill vs-wishlist__stock-text"
            data-vs-stock
            data-key="${escapeHTML(key)}"
          >
            Checking...
          </span>
        </div>

        <!-- ACTIONS -->
        <div class="vs-wishlist__actions">
          <button
            type="button"
            class="vs-wishlist__action-button vs-wishlist__action vs-wishlist__action--cart vs-wishlist__action-button--cart"
            data-vs-add-cart
            data-key="${escapeHTML(key)}"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M3 4h2l2.2 10.1a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 1.9-1.4L20 8H6"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle cx="10" cy="19" r="1.2" fill="currentColor"/>
              <circle cx="18" cy="19" r="1.2" fill="currentColor"/>
            </svg>
            <span>${escapeHTML(addText)}</span>
          </button>

          <button
            type="button"
            class="vs-wishlist__action-button vs-wishlist__action vs-wishlist__action--buy vs-wishlist__action-button--buy codk-buy-now"
            data-vs-buy-now
            data-event-listener="true"
            data-variant-id="${escapeHTML(item.variantId || '')}"
            data-key="${escapeHTML(key)}"
          >
            <span>${escapeHTML(buyText)}</span>
          </button>

          <button
            type="button"
            class="vs-wishlist__remove"
            data-vs-remove
            data-key="${escapeHTML(key)}"
            aria-label="Remove ${title} from wishlist"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6 6 18"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    `;
  }


  /* =========================================================
     RENDER
  ========================================================= */

  function render(root) {
    if (!root) return;

    const grid = root.querySelector('[data-vs-wishlist-grid]');
    const panel = root.querySelector('[data-vs-wishlist-panel]');
    const empty = root.querySelector('[data-vs-wishlist-empty]');

    const items = getWishlist();

    updateWishlistCount();

    if (!items.length) {
      if (grid) grid.innerHTML = '';
      if (panel) {
        panel.hidden = true;
        panel.setAttribute('hidden', '');
      }
      if (empty) {
        empty.hidden = false;
        empty.removeAttribute('hidden');
      }
      return;
    }

    if (empty) {
      empty.hidden = true;
      empty.setAttribute('hidden', '');
    }
    if (panel) {
      panel.hidden = false;
      panel.removeAttribute('hidden');
    }

    if (grid) {
      grid.innerHTML = items.map((item, index) => createRow(item, index, root)).join('');
    }

    syncSelection(root);
    updateTotal(root, items);

    // Resolve stock and missing details asynchronously without loops
    updateStockAndDetails(root, items);
  }


  /* =========================================================
     ENHANCE DETAILS & STOCK (NO INFINITE LOOP)
  ========================================================= */

  async function updateStockAndDetails(root, items) {
    if (isUpdatingStock) return;
    isUpdatingStock = true;

    try {
      const rows = Array.from(root.querySelectorAll('[data-vs-wishlist-row]'));

      await Promise.all(
        items.map(async (item, index) => {
          const row = rows[index];
          if (!row) return;

          const stock = row.querySelector('[data-vs-stock]');
          const buyButton = row.querySelector('[data-vs-buy-now]');

          // Keep COD King hooks present before any user click.
          if (buyButton) {
            buyButton.classList.add('codk-buy-now');
            buyButton.setAttribute('data-event-listener', 'true');
            if (item.variantId) {
              buyButton.setAttribute('data-variant-id', String(item.variantId));
            }
          }

          // Only fetch full product if title, price, or image is missing or placeholder
          if (!item.title || !item.price || parsePrice(item.price) < 1 || item.title === 'Wishlist Product' || !item.image) {
            const product = await fetchProductData(item, root);
            if (product) {
              if (!item.title || item.title === 'Wishlist Product') {
                item.title = product.title;
                const titleEl = row.querySelector('[data-vs-product-title]');
                if (titleEl) {
                  titleEl.textContent = product.title;
                  if (product.url) titleEl.href = product.url;
                }
              }

              if (product.price != null && (!item.price || parsePrice(item.price) < 1)) {
                item.price = parsePrice(product.price);
                const priceEl = row.querySelector('[data-vs-product-price]');
                if (priceEl) priceEl.textContent = formatMoney(item.price, root);
              }

              if (!item.image && (product.featured_image || product.image)) {
                item.image = product.featured_image || product.image;
                const imageWrap = row.querySelector('.vs-wishlist__image');
                if (imageWrap) {
                  imageWrap.innerHTML = `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.title)}" loading="lazy" class="vs-wishlist__product-image">`;
                  if (product.url) imageWrap.href = product.url;
                }
              }

              if (!item.vendor && product.vendor) {
                item.vendor = product.vendor;
              }
            }
          }

          const variant = await getVariant(item, root);

          // Always update the COD King button with the resolved Shopify variant ID.
          if (buyButton) {
            buyButton.classList.add('codk-buy-now');
            buyButton.setAttribute('data-event-listener', 'true');
            if (variant && variant.id) {
              buyButton.setAttribute('data-variant-id', String(variant.id));
            } else {
              buyButton.removeAttribute('data-variant-id');
            }
          }

          if (stock) {
            if (variant && variant.available !== false) {
              stock.textContent = root.dataset.vsStockText || 'In stock';
              stock.classList.remove('is-unavailable');
            } else {
              stock.textContent = root.dataset.vsOutText || 'Unavailable';
              stock.classList.add('is-unavailable');
            }
          }
        })
      );

      updateTotal(root, items);
    } finally {
      isUpdatingStock = false;
    }
  }


  /* =========================================================
     SELECTION
  ========================================================= */

  function syncSelection(root) {
    root.querySelectorAll('[data-vs-product-select]').forEach((input) => {
      const key = String(input.dataset.key);
      input.checked = selectedItems.has(key);
    });

    updateSelectionUI(root);
  }

  function updateSelectionUI(root) {
    const checkboxes = Array.from(root.querySelectorAll('[data-vs-product-select]'));
    const checked = checkboxes.filter((input) => input.checked);
    const count = checked.length;

    // Update Select All Checkbox (supports checked & indeterminate)
    root.querySelectorAll('[data-vs-select-all]').forEach((input) => {
      input.checked = checkboxes.length > 0 && count === checkboxes.length;
      input.indeterminate = count > 0 && count < checkboxes.length;
    });

    // Update Toolbar selection text
    const selectionInfo = root.querySelector('[data-vs-selection-label]');
    if (selectionInfo) {
      selectionInfo.textContent = `${count} selected`;
    }

    // Update Bottom bar selected count
    const bottomSelectedCount = root.querySelector('[data-vs-selected-count]');
    if (bottomSelectedCount) {
      bottomSelectedCount.textContent = count;
    }

    // Enable/Disable Remove Selected button
    const removeButton = root.querySelector('[data-vs-remove-selected]');
    if (removeButton) {
      removeButton.disabled = count === 0;
    }

    // Enable/Disable Add Selected button
    const addSelectedButton = root.querySelector('[data-vs-add-selected]');
    if (addSelectedButton) {
      addSelectedButton.disabled = count === 0;
    }
  }


  /* =========================================================
     TOTAL
  ========================================================= */

  function updateTotal(root, items) {
    const totalElement = root.querySelector('[data-vs-wishlist-total]');
    if (!totalElement) return;

    let total = 0;
    const activeItems = selectedItems.size > 0
      ? items.filter((item, index) => selectedItems.has(productKey(item, index)))
      : items;

    activeItems.forEach((item, index) => {
      const key = productKey(item, index);
      const price = parsePrice(item.price);
      const qty = quantities[key] || 1;
      total += price * qty;
    });

    totalElement.textContent = formatMoney(total, root);
  }


  /* =========================================================
     REMOVE PRODUCT
  ========================================================= */

  function removeProduct(root, key) {
    let items = getWishlist();
    const target = String(key);

    items = items.filter((item, index) => productKey(item, index) !== target);

    selectedItems.delete(target);
    delete quantities[target];

    saveWishlist(items, true);
    render(root);
    showToast(root, 'Removed from wishlist');
  }

  function removeSelected(root) {
    if (!selectedItems.size) return;

    const items = getWishlist();
    const newItems = items.filter((item, index) => !selectedItems.has(productKey(item, index)));

    selectedItems.clear();
    saveWishlist(newItems, true);
    render(root);
    showToast(root, 'Selected items removed');
  }

  function selectAll(root, checked) {
    const items = getWishlist();

    if (checked) {
      items.forEach((item, index) => {
        selectedItems.add(productKey(item, index));
      });
    } else {
      selectedItems.clear();
    }

    syncSelection(root);
    updateTotal(root, items);
  }


  /* =========================================================
     CART ACTIONS
  ========================================================= */

  async function addProductToCart(root, item, button, buyNow, key) {
    if (!item) return;

    const variant = await getVariant(item, root);

    if (!variant || !variant.id || variant.available === false) {
      showToast(root, root.dataset.vsOutText || 'Product unavailable');
      return;
    }

    const itemKey = key || productKey(item, 0);
    const quantity = Math.max(1, Number(quantities[itemKey] || 1));

    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }

    try {
      const response = await fetch(getCartAddUrl(root), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variant.id),
              quantity
            }
          ]
        })
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'Cart error');
      }

      if (buyNow) {
        window.location.href = `${getRoot(root)}checkout`;
        return;
      }

      if (button) {
        button.classList.remove('is-loading');
        button.classList.add('is-success');

        const span = button.querySelector('span');
        const origText = span ? span.textContent : button.textContent;
        if (span) span.textContent = 'Added ✓';

        setTimeout(() => {
          button.disabled = false;
          button.classList.remove('is-success');
          if (span) span.textContent = origText;
        }, 1500);
      }

      dispatchCartEvents(data);
      showToast(root, 'Added to cart');
    } catch (error) {
      console.error('VS Wishlist Cart Error:', error);
      if (button) {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
      showToast(root, error.message || 'Unable to add product');
    }
  }

  async function addSelectedToCart(root) {
    const items = getWishlist();
    const selected = items
      .map((item, index) => ({ item, key: productKey(item, index) }))
      .filter(({ key }) => selectedItems.has(key));

    if (!selected.length) {
      showToast(root, 'Select at least one product');
      return;
    }

    const button = root.querySelector('[data-vs-add-selected]');
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }

    try {
      const cartItems = [];

      for (const { item, key } of selected) {
        const variant = await getVariant(item, root);
        if (!variant || !variant.id || variant.available === false) continue;

        cartItems.push({
          id: Number(variant.id),
          quantity: Math.max(1, Number(quantities[key] || 1))
        });
      }

      if (!cartItems.length) {
        throw new Error('No selected products are available');
      }

      const response = await fetch(getCartAddUrl(root), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'Unable to add selected products');
      }

      dispatchCartEvents(data);
      showToast(root, 'Selected products added to cart');
    } catch (error) {
      console.error('VS Selected Cart Error:', error);
      showToast(root, error.message || 'Unable to add products');
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  async function addAllToCart(root, button) {
    const items = getWishlist();
    if (!items.length) {
      showToast(root, 'Your wishlist is empty');
      return;
    }

    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }

    try {
      const cartItems = [];

      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const key = productKey(item, index);
        const variant = await getVariant(item, root);
        if (!variant || !variant.id || variant.available === false) continue;

        cartItems.push({
          id: Number(variant.id),
          quantity: Math.max(1, Number(quantities[key] || 1))
        });
      }

      if (!cartItems.length) {
        throw new Error('No wishlist products are currently available');
      }

      const response = await fetch(getCartAddUrl(root), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'Unable to add all products to cart');
      }

      dispatchCartEvents(data);
      showToast(root, 'All products added to cart');
    } catch (error) {
      console.error('VS Add All Cart Error:', error);
      showToast(root, error.message || 'Unable to add all products');
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  function dispatchCartEvents(detail) {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: detail } }));
    document.dispatchEvent(new CustomEvent('vs:cart-updated', { detail: { item: detail } }));
    window.dispatchEvent(new CustomEvent('vs:cart-updated', { detail: { item: detail } }));
  }


  /* =========================================================
     TOAST
  ========================================================= */

  function showToast(root, message) {
    const toast =
      root?.querySelector('[data-vs-wishlist-toast]') ||
      document.querySelector('[data-vs-wishlist-toast]');

    if (toast) {
      toast.textContent = message;
      toast.hidden = false;
      toast.removeAttribute('hidden');

      void toast.offsetWidth;
      toast.classList.add('is-visible');

      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => {
          if (!toast.classList.contains('is-visible')) {
            toast.hidden = true;
            toast.setAttribute('hidden', '');
          }
        }, 220);
      }, 2500);

      return;
    }

    let fallback = document.querySelector('.vs-wishlist-fallback-toast');
    if (!fallback) {
      fallback = document.createElement('div');
      fallback.className = 'vs-wishlist-fallback-toast';
      Object.assign(fallback.style, {
        position: 'fixed',
        right: '20px',
        bottom: '20px',
        zIndex: '999999',
        background: '#151515',
        color: '#fff',
        padding: '13px 18px',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: '700',
        boxShadow: '0 15px 40px rgba(0,0,0,.2)'
      });
      document.body.appendChild(fallback);
    }

    fallback.textContent = message;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      fallback.remove();
    }, 2500);
  }


  /* =========================================================
     EVENT BINDING
  ========================================================= */

  function bindRoot(root) {
    if (!root) return;
    if (root.dataset.vsWishlistBound === 'true') {
      render(root);
      return;
    }
    root.dataset.vsWishlistBound = 'true';

    // Click events
    root.addEventListener('click', (event) => {
      // Remove single product
      const removeBtn = event.target.closest('[data-vs-remove]');
      if (removeBtn) {
        event.preventDefault();
        removeProduct(root, removeBtn.dataset.key);
        return;
      }

      // Quantity Minus
      const minusBtn = event.target.closest('[data-vs-quantity-minus]');
      if (minusBtn) {
        event.preventDefault();
        const key = minusBtn.dataset.key;
        quantities[key] = Math.max(1, Number(quantities[key] || 1) - 1);
        const input = root.querySelector(`[data-vs-quantity][data-key="${CSS.escape(key)}"]`);
        if (input) input.value = quantities[key];
        updateTotal(root, getWishlist());
        return;
      }

      // Quantity Plus
      const plusBtn = event.target.closest('[data-vs-quantity-plus]');
      if (plusBtn) {
        event.preventDefault();
        const key = plusBtn.dataset.key;
        quantities[key] = Math.min(99, Number(quantities[key] || 1) + 1);
        const input = root.querySelector(`[data-vs-quantity][data-key="${CSS.escape(key)}"]`);
        if (input) input.value = quantities[key];
        updateTotal(root, getWishlist());
        return;
      }

      // Add single product to cart
      const cartBtn = event.target.closest('[data-vs-add-cart]');
      if (cartBtn) {
        event.preventDefault();
        const items = getWishlist();
        const key = String(cartBtn.dataset.key);
        const index = items.findIndex((it, idx) => productKey(it, idx) === key);
        if (index >= 0) {
          addProductToCart(root, items[index], cartBtn, false, key);
        }
        return;
      }

      // Buy Now single product
      const buyBtn = event.target.closest('[data-vs-buy-now]');
      if (buyBtn) {
        const items = getWishlist();
        const key = String(buyBtn.dataset.key);
        const index = items.findIndex((it, idx) => productKey(it, idx) === key);

        if (index < 0) return;

        /*
         * COD King must receive the original click.
         * If the variant ID is already resolved, do not prevent the event
         * and do not call Shopify /cart/add.js ourselves.
         */
        if (
          buyBtn.classList.contains('codk-buy-now') &&
          buyBtn.getAttribute('data-event-listener') === 'true' &&
          buyBtn.getAttribute('data-variant-id')
        ) {
          return;
        }

        /*
         * The wishlist is rendered from localStorage, so variant resolution
         * can finish after the first paint. Resolve it before handing the
         * click to COD King, then replay the click with the correct variant.
         */
        event.preventDefault();

        const item = items[index];
        buyBtn.classList.add('codk-buy-now');
        buyBtn.setAttribute('data-event-listener', 'true');
        buyBtn.disabled = true;

        getVariant(item, root)
          .then((variant) => {
            if (!variant || !variant.id || variant.available === false) {
              throw new Error(root.dataset.vsOutText || 'Product unavailable');
            }

            buyBtn.setAttribute('data-variant-id', String(variant.id));

            // Re-enable and replay only after the correct variant is attached.
            buyBtn.disabled = false;
            buyBtn.click();
          })
          .catch((error) => {
            buyBtn.disabled = false;
            console.error('VS Wishlist COD King Variant Error:', error);
            showToast(root, error.message || 'Product unavailable');
          });

        return;
      }

      // Add Selected to cart
      const addSelectedBtn = event.target.closest('[data-vs-add-selected]');
      if (addSelectedBtn) {
        event.preventDefault();
        addSelectedToCart(root);
        return;
      }

      // Add All to cart
      const addAllBtn = event.target.closest('[data-vs-add-all]');
      if (addAllBtn) {
        event.preventDefault();
        addAllToCart(root, addAllBtn);
        return;
      }

      // Remove Selected
      const removeSelectedBtn = event.target.closest('[data-vs-remove-selected]');
      if (removeSelectedBtn) {
        event.preventDefault();
        removeSelected(root);
        return;
      }
    });

    // Checkbox change events
    root.addEventListener('change', (event) => {
      const checkbox = event.target.closest('[data-vs-product-select]');
      if (checkbox) {
        const key = String(checkbox.dataset.key);
        if (checkbox.checked) {
          selectedItems.add(key);
        } else {
          selectedItems.delete(key);
        }
        updateSelectionUI(root);
        updateTotal(root, getWishlist());
        return;
      }

      const selectAllInput = event.target.closest('[data-vs-select-all]');
      if (selectAllInput) {
        selectAll(root, selectAllInput.checked);
        return;
      }
    });

    // Quantity manual input
    root.addEventListener('input', (event) => {
      const input = event.target.closest('[data-vs-quantity]');
      if (!input) return;

      const key = input.dataset.key;
      let val = parseInt(input.value, 10);
      if (!Number.isFinite(val) || val < 1) val = 1;
      if (val > 99) val = 99;

      quantities[key] = val;
      updateTotal(root, getWishlist());
    });

    // Initial render
    render(root);
  }


  /* =========================================================
     INIT & LIFECYCLE (DEBOUNCED)
  ========================================================= */

  function init() {
    clearTimeout(updateDebounceTimer);
    updateDebounceTimer = setTimeout(() => {
      document.querySelectorAll('[data-vs-wishlist]').forEach((root) => {
        bindRoot(root);
      });
      updateWishlistCount();
    }, 40);
  }

  // Listen to external wishlist updates (debounced)
  document.addEventListener('vs:wishlist:updated', () => {
    init();
  });

  // Cross-tab sync
  window.addEventListener('storage', (event) => {
    if (STORAGE_KEYS.includes(event.key)) {
      selectedItems.clear();
      init();
    }
  });

  // Shopify Theme Editor support
  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event.target?.querySelector('[data-vs-wishlist]');
    if (sectionRoot) {
      delete sectionRoot.dataset.vsWishlistBound;
      bindRoot(sectionRoot);
    } else {
      init();
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const sectionRoot = event.target?.querySelector('[data-vs-wishlist]');
    if (sectionRoot) bindRoot(sectionRoot);
  });

  document.addEventListener('shopify:section:reorder', () => {
    init();
  });

  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
